/**
 * New support request (CLAUDE.md 12.3).
 *
 * The feature that makes this worth building rather than pointing at an email address:
 * a request can carry a **frozen snapshot** of a position -- module, version, code and
 * the full input JSON. A bug report about a calculation without its inputs is close to
 * useless, and asking an already-annoyed engineer to transcribe forty numbers is how
 * you get a report you cannot reproduce.
 *
 * Attachments upload *after* the ticket exists, against its id. If an upload fails
 * the request still stands and the thread page says which files did not make it, so
 * a flaky connection costs a retry, not the whole report.
 */
import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import {
  supportKeys,
  uploadAttachments,
  useCreateTicket,
  useTicketLimits,
  type TicketType,
} from '@/api/support'
import { WorkspaceChrome } from '@/components/layout/WorkspaceChrome'
import { AttachmentPicker, useAttachmentDraft } from '@/components/support/AttachmentPicker'
import { MarkdownEditor } from '@/components/support/MarkdownEditor'
import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { Field, FieldLabel, useFieldControlProps } from '@/components/ui/field'
import { FieldInput } from '@/components/ui/input'
import { useToast } from '@/components/ui/toast'

const TYPES: TicketType[] = ['issue', 'feature', 'question']

export function SupportNewPage() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [params] = useSearchParams()

  // A "report a problem with this calculation" link from the position page arrives
  // with the position id, so the snapshot is attached without the user hunting for it.
  const positionParam = params.get('position')
  const positionId =
    positionParam && Number.isFinite(Number(positionParam)) ? Number(positionParam) : null

  const limits = useTicketLimits()
  const draft = useAttachmentDraft(limits.data)
  const create = useCreateTicket()

  const [type, setType] = useState<TicketType>(positionId ? 'issue' : 'question')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [attach, setAttach] = useState(positionId !== null)
  const [uploading, setUploading] = useState(false)

  const busy = create.isPending || uploading

  const submit = async (event?: FormEvent) => {
    event?.preventDefault()
    if (busy || !title.trim() || !body.trim()) return

    let ticketId: number
    try {
      const ticket = await create.mutateAsync({
        type,
        title: title.trim(),
        body,
        position_id: attach && positionId ? positionId : undefined,
      })
      ticketId = ticket.id
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof ApiError ? error.localised : undefined,
        variant: 'error',
      })
      return
    }

    if (draft.files.length > 0) {
      setUploading(true)
      try {
        await uploadAttachments(ticketId, draft.files)
      } catch (error) {
        toast({
          title: t('support.attachments.upload_failed'),
          description: error instanceof ApiError ? error.localised : undefined,
          variant: 'error',
        })
      } finally {
        setUploading(false)
        void queryClient.invalidateQueries({ queryKey: supportKeys.all })
      }
    }

    toast({ title: t('support.created'), variant: 'success' })
    navigate(`/support/${ticketId}`)
  }

  return (
    <WorkspaceChrome title={t('support.new')}>
      <div className="mx-auto max-w-3xl space-y-4 p-4">
        <Link
          to="/support"
          className="inline-flex h-9 items-center gap-1 text-sm text-text-muted hover:text-text"
        >
          <ChevronLeft size={15} aria-hidden />
          {t('support.title')}
        </Link>
        <h2 className="text-lg font-semibold">{t('support.new')}</h2>

        <Card>
          <CardBody>
            <form onSubmit={submit} className="space-y-5">
              <Field>
                <FieldLabel>{t('support.type')}</FieldLabel>
                <TypeSelect value={type} onChange={setType} />
              </Field>

              <Field>
                <FieldLabel>{t('support.subject')}</FieldLabel>
                <FieldInput
                  required
                  minLength={3}
                  maxLength={200}
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder={t('support.subject_placeholder')}
                />
              </Field>

              <Field description={t('support.message_hint')}>
                <FieldLabel>{t('support.message')}</FieldLabel>
                <MarkdownEditor
                  value={body}
                  onChange={setBody}
                  onPasteFiles={draft.add}
                  onSubmit={() => void submit()}
                  rows={8}
                  required
                  placeholder={t('support.message_placeholder')}
                  disabled={busy}
                />
              </Field>

              <Field>
                <FieldLabel>{t('support.attachments.title')}</FieldLabel>
                <AttachmentPicker draft={draft} disabled={busy} />
              </Field>

              {positionId !== null && (
                <Field description={t('support.attach_hint')}>
                  <div className="flex items-center gap-2 text-sm">
                    <input
                      id="attach-position"
                      type="checkbox"
                      checked={attach}
                      onChange={(event) => setAttach(event.target.checked)}
                    />
                    <label htmlFor="attach-position">{t('support.attach_position')}</label>
                  </div>
                </Field>
              )}

              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button asChild type="button" disabled={busy}>
                  <Link to="/support">{t('action.cancel')}</Link>
                </Button>
                <Button type="submit" variant="primary" loading={busy}>
                  {uploading ? t('support.attachments.uploading') : t('action.submit')}
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      </div>
    </WorkspaceChrome>
  )
}

/** Inside a `<Field>`, so the label points at the control (14.5). */
function TypeSelect({
  value,
  onChange,
}: {
  value: TicketType
  onChange: (value: TicketType) => void
}) {
  const { t } = useTranslation()
  const control = useFieldControlProps()
  return (
    <select
      {...control}
      value={value}
      onChange={(event) => onChange(event.target.value as TicketType)}
      className="h-11 w-full rounded-md border border-border bg-surface px-3 text-text"
    >
      {TYPES.map((option) => (
        <option key={option} value={option}>
          {t(`support.type.${option}`)}
        </option>
      ))}
    </select>
  )
}
